import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { profile, activeSession, muscleName, muscleByName, getBodyMap } from "../store.js";
import { navigate } from "../core/router.js";
import { icon, achIcon } from "../lib/icons.js";
import { topbar, exerciseThumb } from "../components/common.js";
import { MuscleMap } from "../components/MuscleMap.js";
import { spinner, errorState, toast } from "../components/ui.js";
import { fmtVolume, fmtDurationLong, relativeDay, fmtWeight } from "../lib/format.js";
import { strengthLevel } from "../lib/standards.js";
import { coachCard } from "../components/coachCard.js";
import { bowProgress } from "../components/bowProgress.js";
import { lineChart } from "../components/Chart.js";
import { homeContext, HOME_TOOLS, milestoneCollection, nextMilestone } from "../lib/training.js";
import { editWeeklyPlan, editHomeTools } from "../components/trainingPlan.js";
import { weeklyReviewCard } from "../components/weeklyReview.js";
import { avatar } from "./Friends.js";

export default function Dashboard() {
  const root = h("div", { class: "screen dashboard-screen" });
  load();

  async function load() {
    const greeting = greetingText();
    const name = profile()?.display_name || profile()?.username || "there";
    setChildren(root, topbar(`${greeting}, ${name}`, { sub: "Ready to train?" }), spinner());
    try {
      // One aggregate round-trip (see GET /api/dashboard) instead of 7 parallel calls.
      const d = await api.dashboard();
      render(d.stats, d.recovery, d.records, name, greeting, d.achievements, d.friend_activity, d.bodyweight, d.program_today, d.coverage, d.coaching, d.training_plan, d.weekly_review);
    } catch (e) {
      setChildren(root, topbar("Home"), errorState(e.message, load));
    }
  }

  function render(stats, recovery, records, name, greeting, achievements, activity, weight, program, coverage, coaching, plan, review) {
    const recMap = new Map();
    for (const r of recovery) {
      const n = muscleName(r.muscle_id);
      if (n) recMap.set(n.toLowerCase(), r.days_since);
    }
    // Muscles only ever worked as a secondary mover — shown as a distinct "indirect"
    // tier so a grey muscle you actually hit (e.g. forearms on every pull) reads apart
    // from one you've genuinely never trained.
    const indirect = [];
    for (const c of coverage || []) {
      if (c.is_secondary && !c.is_primary) { const n = muscleName(c.muscle_id); if (n) indirect.push(n.toLowerCase()); }
    }

    const context = homeContext({active:activeSession(),stats,program,plan});
    const activate = () => {
      if(context.kind === "active") return navigate("/session");
      if(context.kind === "completed") return navigate(`/history/session/${stats.last_session.id}`);
      if(context.kind === "paused") return editWeeklyPlan(load);
      if(context.kind === "scheduled") return beginProgram(program.today.workout.id, program.id);
      if(context.kind === "intention" && plan.intention.workout_id) return beginProgram(plan.intention.workout_id, null);
      if(context.kind === "rest") return plan?.intention ? editWeeklyPlan(load) : navigate("/programs");
      navigate("/start");
    };
    setChildren(root,
      h("div",{class:"dashboard-masthead"},h("span",{class:"eyebrow"},"TODAY AT A GLANCE"),h("span",{class:"dashboard-date"},new Intl.DateTimeFormat(undefined,{weekday:"short",month:"short",day:"numeric"}).format(new Date()))),
      topbar(`${greeting}, ${name}`,{actions:h("button",{class:"icon-btn ghost","aria-label":"Your profile",onClick:()=>navigate("/profile")},avatar(profile(),38))}),
      h("section",{class:"training-hero contextual-hero",dataset:{state:context.kind},"aria-label":"Your next step"},
        h("span",{class:"eyebrow"},context.label),h("h2",null,context.title),h("p",{class:"today-line"},context.text),
        h("button",{class:"btn btn-primary btn-block btn-lg",onClick:activate},icon(context.kind==="completed"?"check":"forward",{size:20}),context.action),
        ["completed","paused","rest"].includes(context.kind)?h("button",{class:"link-btn secondary-start",onClick:()=>navigate("/start")},context.kind==="completed"?"Start another workout":"Choose a workout anyway"):null),
      weeklyGoal(stats),
      weeklyReviewCard(review,{compact:true}),
      h("div",{class:"home-tools-head"},h("span",{class:"section-title"},"Your shortcuts"),h("button",{class:"link-btn",onClick:()=>editHomeTools(load)},"Customize")),
      h("nav",{class:"daily-tools chosen-tools","aria-label":"Quick tracking"},...(profile()?.home_tools || ["weight","measurements"]).filter(key=>HOME_TOOLS[key]).map(key=>quickTool(...HOME_TOOLS[key]))),
      h("details",{class:"home-details",open:true},h("summary",null,"Training insights & more"),
        h("div",{class:"stat-grid mt"},metricCard("flame",String(stats.week_streak || 0),"completed goal weeks in continuity","Consistency","/week","bronze"),metricCard("dumbbell",String(stats.total_sessions),"workouts completed","All time","/history")),
        h("div",{class:"dashboard-columns"},
          h("div",{class:"dashboard-guidance"},program?.today?programCard(program,{hideStart:true}):null,coachCard(coaching),recordsCard(records)),
          h("section",{class:"dashboard-recovery"},h("div",{class:"section-title row-between"},"Muscle recovery",h("span",{class:"recovery-label"},"TIME SINCE TRAINING")),
            h("div",{class:"card"},recMap.size || indirect.length?recoveryMap(recMap,indirect):h("div",{class:"recovery-empty"},MuscleMap({}),h("p",{class:"dim center"},"Your muscle map comes to life after your first workout."))))),
        secondaryModules(activity,weight,achievements)));
  }

  // "Today's workout" from the user's active program. With `hideStart` the main dashboard CTA
  // already starts this day, so the card is a pure preview (tap body to expand the exercises).
  function programCard(program, { hideStart = false } = {}) {
    const d = program.today;
    const header = h("div", { class: "section-title row-between" },
      h("span", null, program.name),
      h("button", { class: "link-btn dim", style: { fontSize: ".82rem" }, onClick: () => navigate("/programs") }, "Program")
    );

    if (!d.workout) {
      const next = program.next ? h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } }, `Up next: ${program.next.workout.name}`) : null;
      // Calendar-anchored programs advance rest days by date; only manual ones need "Mark done".
      const restAction = program.anchored
        ? h("span", { class: "dim", style: { fontSize: ".82rem" } }, "Rest day")
        : h("button", { class: "btn btn-ghost", onClick: () => advanceProgram(program.id) }, "Mark done", icon("forward", { size: 16 }));
      return h("div", null, header,
        h("div", { class: "card row-between", style: { alignItems: "center" } },
          h("div", null, h("div", { style: { fontWeight: 700 } }, d.label || "Rest day"), h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } }, "Recovery — no workout scheduled today"), next),
          restAction
        )
      );
    }

    const exercises = d.workout.exercises || [];
    const primary = exercises.flatMap((e) => e.primary_muscle_ids || []);
    const mini = MuscleMap({ primary });
    mini.classList.add("mini");
    mini.style.maxWidth = "92px";
    const action = program.anchored && program.done_today
      ? h("span", { class: "hstack", style: { gap: "6px", color: "var(--success)", fontWeight: 700, fontSize: ".88rem" } }, icon("check", { size: 18 }), "Done")
      : hideStart
        ? null
        : h("button", { class: "btn btn-primary", onClick: () => beginProgram(d.workout.id, program.id) }, icon("play", { size: 18, fill: true }), "Start");

    // Tap the card body to preview the day's exercises before starting.
    const preview = h("div", { hidden: true, style: { marginTop: "12px", paddingTop: "10px", borderTop: "1px solid var(--border)" } },
      ...exercises.map(exercisePreviewRow)
    );
    const chev = icon("chevronDown", { size: 16, className: "icon muted" });
    chev.style.transition = "transform .15s ease";
    const toggle = () => {
      const open = preview.hidden;
      preview.hidden = !open;
      chev.style.transform = open ? "rotate(180deg)" : "";
    };
    const body = h("div", { class: "hstack grow card-tap", style: { alignItems: "center", gap: "12px" }, onClick: toggle },
      mini,
      h("div", { class: "grow" },
        h("div", { class: "dim", style: { fontSize: ".7rem", fontWeight: 700, letterSpacing: ".05em" } }, "TODAY"),
        h("div", { style: { fontWeight: 700, fontSize: "1.05rem" } }, d.label || d.workout.name),
        h("div", { class: "hstack dim", style: { fontSize: ".82rem", gap: "4px", alignItems: "center" } },
          `${exercises.length} exercise${exercises.length === 1 ? "" : "s"}`, chev)
      )
    );
    return h("div", null, header,
      h("div", { class: "card" },
        h("div", { class: "hstack", style: { alignItems: "center" } }, body, action),
        preview
      )
    );
  }

  function exercisePreviewRow(ex) {
    const sets = ex.target_sets || 3;
    const reps = ex.target_reps_min
      ? ` × ${ex.target_reps_min}${ex.target_reps_max && ex.target_reps_max !== ex.target_reps_min ? `–${ex.target_reps_max}` : ""}`
      : "";
    const setsLabel = `${sets} set${sets === 1 ? "" : "s"}${reps}`;
    return h("div", { class: "hstack", style: { gap: "10px", alignItems: "center", padding: "7px 0" } },
      exerciseThumb(ex.images, { size: 38 }),
      h("div", { class: "grow" },
        h("div", { style: { fontWeight: 600, fontSize: ".92rem" } }, ex.exercise_name),
        h("div", { class: "dim", style: { fontSize: ".8rem" } }, setsLabel)
      )
    );
  }

  async function beginProgram(workoutId, programId) {
    if (activeSession()) return navigate("/session");
    try {
      const session = await api.startSession({ workout_id: workoutId, program_id: programId });
      activeSession(session);
      navigate("/session");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  async function advanceProgram(programId) {
    try {
      await api.advanceProgram(programId);
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  function friendsCard(activity) {
    const now = activity?.training_now || [];
    const recent = activity?.recent || [];
    const header = h("div", { class: "section-title row-between" },
      h("span", null, "Friends"),
      h("button", { class: "link-btn dim", style: { fontSize: ".82rem" }, onClick: () => navigate("/friends") }, "See all")
    );
    if (!now.length && !recent.length) {
      return h("div", null, header,
        h("div", { class: "card card-tap row-between", onClick: () => navigate("/friends") },
          h("div", null, h("div", { style: { fontWeight: 600 } }, "Find people to follow"), h("div", { class: "dim", style: { fontSize: ".84rem", marginTop: "2px" } }, "Follow people to see their workouts and progress.")),
          icon("forward", { size: 20, className: "icon muted" })
        )
      );
    }
    const rows = [];
    for (const a of now.slice(0, 3)) {
      rows.push(h("div", { class: "row-between card-tap", style: { padding: "8px 0" }, onClick: () => navigate(`/friends/${a.user.id}`) },
        h("div", { class: "hstack", style: { gap: "10px", alignItems: "center" } }, avatar(a.user, 36),
          h("div", null, h("div", { style: { fontWeight: 600 } }, a.user.display_name || a.user.username),
            h("div", { class: "hstack", style: { gap: "6px", color: "var(--success)", fontSize: ".82rem", marginTop: "2px" } }, h("span", { class: "pulse" }), "Training now"))),
      ));
    }
    for (const a of recent.slice(0, now.length ? 2 : 3)) {
      rows.push(h("div", { class: "row-between card-tap", style: { padding: "8px 0" }, onClick: () => navigate(`/friends/${a.user.id}`) },
        h("div", { class: "hstack", style: { gap: "10px", alignItems: "center" } }, avatar(a.user, 36),
          h("div", null, h("div", { style: { fontWeight: 600 } }, a.user.display_name || a.user.username),
            h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } }, `${a.name || "Workout"} · ${relativeDay(a.ended_at || a.started_at).toLowerCase()}`))),
        a.kudos_count ? h("span", { class: "dim", style: { fontSize: ".8rem" } }, `👏 ${a.kudos_count}`) : null
      ));
    }
    return h("div", null, header, h("div", { class: "card" }, ...rows));
  }

  function weightCard(weight) {
    const header = h("div", { class: "section-title row-between" },
      h("span", null, "Bodyweight"),
      h("button", { class: "link-btn dim", style: { fontSize: ".82rem" }, onClick: () => navigate("/weight") }, "Open")
    );
    const cur = weight?.current_kg;
    const recent = (weight?.entries || []).slice(-14);
    if (cur != null) {
      return h("div", null, header,
        h("div", { class: "card" },
          h("div", { class: "weight-snapshot" }, h("strong", null, fmtWeight(cur)), h("span", null, weight.logged_today ? "Logged today" : recent.length ? relativeDay(recent[recent.length - 1].logged_at) : "Latest weight")),
          recent.length > 1 ? h("div", { class: "weight-sparkline", role: "img", "aria-label": `Weight trend across your last ${recent.length} weigh-ins, from ${fmtWeight(recent[0].weight_kg)} to ${fmtWeight(cur)}` },
            lineChart(recent.map(entry => ({ x: entry.logged_at, y: entry.weight_kg })), { height: 90, format: fmtWeight })) : null,
          h("div", { class: "row-between mt" }, h("span", { class: "dim" }, recent.length > 1 ? `Last ${recent.length} weigh-ins` : "Your first step recorded"),
            h("button", { class: "btn btn-ghost btn-sm", onClick: () => navigate("/weight?log=1") }, icon("plus", { size: 15 }), "Log weight"))));
    }
    return h("div", null, header,
      h("div", { class: "card card-tap row-between", style: { alignItems: "center" }, onClick: () => navigate("/weight?log=1") },
        h("div", { class: "hstack", style: { gap: "10px", alignItems: "center" } },
          icon("weight", { size: 20, className: "icon muted" }),
          h("div", null,
            h("div", { style: { fontWeight: 600 } }, "Log your first weigh-in"),
            h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } }, "See your trend over time"))
        ),
        icon("forward", { size: 20, className: "icon muted" })
      )
    );
  }

  function secondaryModules(activity, weight, achievements) {
    return h("div", { class: "dashboard-life" },
      weightCard(weight),
      achievements ? achievementsCard(achievements) : null,
      friendsCard(activity)
    );
  }

  function quickTool(ic, label, path) {
    return h("button", { class: "daily-tool", onClick: () => navigate(path) }, icon(ic, { size: 19 }), h("span", null, label));
  }

  function metricCard(ic, value, label, title, path, tone = "") {
    return h("button", { class: ["stat", "metric-card", tone], onClick: () => navigate(path) },
      h("span", { class: "metric-heading" }, icon(ic, { size: 17 }), title),
      h("span", { class: "num" }, value), h("span", { class: "lbl" }, label));
  }

  function weeklyGoal(stats) {
    const done=stats.this_week_sessions || 0, goal=stats.weekly_goal || 3;
    return h("div",{class:"weekly-plan-line"},
      bowProgress(stats.week_paused?0:Math.min(1,done/goal),{size:30}),
      h("div",{class:"grow"},h("strong",null,stats.week_paused?"Weekly goal paused":`${done} of ${goal} workouts this week`),h("span",{class:"dim"},stats.week_paused?"Continuity preserved · no goal week earned":done>=goal?"Your chosen target is complete":"A plan you can adjust")),
      h("button",{class:"btn btn-ghost btn-sm",onClick:()=>editWeeklyPlan(load)},"Adjust plan"));
  }

  function achievementsCard(achievements) {
    const earned = achievements.badges.filter((b) => b.earned);
    const next = nextMilestone(achievements.badges, milestoneCollection(profile()));
    return h("div", null,
      h("div", { class: "section-title" }, "Achievements"),
      h("div", { class: "card card-tap row-between", onClick: () => navigate("/achievements") },
        h("div", { class: "grow" },
          h("div", { style: { fontWeight: 700 } }, `${achievements.earned} earned milestones`),
          h("div", { class: "ach-strip mt", style: { marginTop: "6px" } },
            ...earned.slice(-5).map((b) => h("span", null, achIcon(b.icon, { size: 24 }))),
            ...(earned.length === 0 ? [h("span", { class: "dim", style: { fontSize: ".85rem" } }, "Log a workout to earn your first badge")] : [])
          ),
          next ? h("div", { class: "milestone mt" }, achIcon(next.icon, { size: 32 }),
            h("div", { class: "milestone-copy" }, h("span", { class: "dim" }, "Next milestone"), h("strong", null, next.title),
              h("div", { class: "progress", role: "progressbar", "aria-label": next.title, "aria-valuenow": Math.round(next.progress * 100), "aria-valuemin": "0", "aria-valuemax": "100" },
                h("i", { style: { width: `${next.progress * 100}%` } })),
              h("span", { class: "dim" }, `${next.value.toLocaleString()} / ${next.target.toLocaleString()}`))) : null
        ),
        icon("forward", { size: 20, className: "icon muted" })
      )
    );
  }

  function recordsCard(records) {
    return h("div", null,
      h("div", { class: "section-title row-between" },
        h("span", null, "Personal records"),
        h("button", { class: "link-btn dim", style: { fontSize: ".82rem" }, onClick: () => navigate("/standards") }, "How levels work")),
      records.length
        ? h("div", { class: "card records-card" }, ...records.slice(0, 3).map(recordRow),
            records.length > 3 ? h("details", { class: "records-more" }, h("summary", null, `View all ${records.length} records`), ...records.slice(3).map(recordRow)) : null)
        : h("div", { class: "card" }, h("p", { class: "dim center" }, "Your best lifts will show up here."))
    );
  }

  function recoveryMap(recMap, indirect) {
    // Regions tap through to the per-muscle drill-down (weekly sets vs landmarks).
    const open = (name) => {
      const m = muscleByName(name);
      if (m) navigate(`/muscle/${m.muscle_id}`);
    };
    const hasDetrained = [...recMap.values()].some((d) => d >= 14);
    return h("div", null,
      MuscleMap({ recovery: recMap, indirect, onRegionClick: open }),
      legend(indirect.length > 0, hasDetrained),
      h("p", { class: "dim center", style: { fontSize: ".74rem", marginTop: "8px", marginBottom: 0 } }, "Tap a muscle for details")
    );
  }

  function legend(showIndirect, hasDetrained = false) {
    const items = [
      ["var(--danger)", "Today", 1],
      ["var(--warn)", "Yesterday", 1],
      ["#eab308", "2–3 days", 1],
      ["var(--success)", hasDetrained ? "4–13 days" : "4+ days", 1],
    ];
    if (hasDetrained) items.push(["#64748b", "14+ days", 0.65]);
    if (showIndirect) items.push(["var(--accent)", "Indirect", 0.28]);
    items.push(["var(--mm-fill)", "Untrained", 1]);
    return h("div", { class: "recovery-legend" }, ...items.map(([c, l, o]) => h("span", null, h("i", { style: { background: c, opacity: String(o) } }), l)));
  }

  function recordRow(r) {
    // Strength-standard context: estimated 1RM as a multiple of current bodyweight (a rough,
    // widely-used guide — only shown when we know the user's weight and it's a meaningful lift).
    const bw = profile()?.bodyweight_kg;
    const ratio = bw && bw > 0 ? r.estimated_1rm_kg / bw : null;
    // For the main barbell lifts, turn the bodyweight ratio into a training-level band.
    const sex = getBodyMap();
    const std = ratio ? strengthLevel(r.exercise_name, ratio, sex) : null;
    return h(
      "div",
      { class: "row-between card-tap", style: { padding: "9px 0" }, onClick: () => navigate(`/exercise/${r.exercise_id}`) },
      h("div", null, h("div", { style: { fontWeight: 700 } }, r.exercise_name), h("div", { class: "dim" }, `${fmtWeight(r.weight_kg)} × ${r.reps} · ${relativeDay(r.date)}`)),
      h("div", { class: "hstack", style: { gap: "6px" } },
        std ? h("span", { class: "badge", style: { background: "var(--surface-2)", color: std.color }, title: `Approx. strength level — ${ratio.toFixed(2)}× bodyweight (rough guide)` }, std.level) : null,
        // ×BW only alongside a level band — on unscored lifts (accessories, unilaterals) the
        // ratio has no standard to compare against and just reads as a badge with no meaning.
        std && ratio ? h("span", { class: "badge", style: { background: "var(--surface-2)", color: "var(--text-2)" }, title: "Estimated 1RM ÷ bodyweight" }, `${ratio.toFixed(2)}× BW`) : null,
        h("span", { class: "badge badge-pr" }, icon("trophy", { size: 14, fill: true }), `${fmtWeight(r.estimated_1rm_kg)} est. 1RM`))
    );
  }

  return root;
}

function greetingText() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

// One generated "today" sentence above the CTA — a read of the whole dashboard payload so
// the user doesn't have to scan the cards: recovery state (legs vs upper body, from the
// primary-mover recovery map), what the program says is due, and a duration estimate from
// their own average session. Pure function of data already fetched; returns null when
// there's nothing worth saying.
const LOWER_MUSCLES = new Set(["quadriceps", "hamstrings", "glutes", "calves", "adductors", "abductors"]);

function todayNarrative(recMap, stats, program, todayDay) {
  // Describe elapsed training time; it is not a measured readiness score.
  let lower = Infinity, upper = Infinity;
  for (const [muscle, days] of recMap) {
    if (LOWER_MUSCLES.has(muscle)) lower = Math.min(lower, days);
    else upper = Math.min(upper, days);
  }
  const phrase = (label, days) =>
    days === 0 ? `${label} trained today` : days === 1 ? `${label} trained yesterday` : `${label} last trained ${days} days ago`;
  let recPart = null;
  if (isFinite(lower) && isFinite(upper)) {
    recPart = lower >= 2 && upper >= 2 ? `last trained ${Math.min(lower, upper)} days ago` : `${phrase("legs", lower)}, ${phrase("upper body", upper)}`;
  } else if (isFinite(lower)) {
    recPart = phrase("legs", lower);
  } else if (isFinite(upper)) {
    recPart = phrase("upper body", upper);
  }

  // Own-history duration estimate, rounded to 5 min — only once there's enough history.
  const avgMin = stats.total_sessions >= 3 ? Math.round(stats.total_duration_seconds / stats.total_sessions / 60 / 5) * 5 : 0;

  let planPart = null;
  if (todayDay) {
    planPart = avgMin >= 10 ? `your workouts average about ${avgMin} min` : null;
  } else if (program && program.anchored && program.done_today) {
    planPart = "today's session is done, nice work";
  } else if (program && program.today && !program.today.workout) {
    planPart = "rest day on your program";
  }

  const parts = [recPart, planPart].filter(Boolean);
  if (!parts.length) return null;
  const s = parts.join(" — ");
  return s.charAt(0).toUpperCase() + s.slice(1) + ".";
}
